import { useCallback, useMemo } from 'react';
import { useQueryState, parseAsString } from 'nuqs';
import { getLocalTimeZone, today, parseDate } from '@internationalized/date';
import { servicesQuery } from '@/services/api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { isEmptyString } from '@/utils/string';
import type { DeliverabilityDateRange, DeliverabilityMetricRow } from './types';
import { transformMetricsToRows, hasNoData } from './utils/metrics';
import { FIRST_POSSIBLE_DATE } from './utils/constants';

const thirtyDaysAgo = today(getLocalTimeZone()).subtract({ days: 30 });

export function useDeliverabilityData() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  // URL state management with nuqs
  const [startTime, setStartTime] = useQueryState('start_time', parseAsString);
  const [endTime, setEndTime] = useQueryState('end_time', parseAsString);
  const [sortBy, setSortBy] = useQueryState('sort_by', parseAsString);
  const [sortDir, setSortDir] = useQueryState('sort_dir', parseAsString);
  const [search, setSearch] = useQueryState('search', parseAsString);

  // Parse date range from URL params
  const dateRange = useMemo<DeliverabilityDateRange>(() => {
    const todayDate = today(getLocalTimeZone());
    let startDate = FIRST_POSSIBLE_DATE.compare(thirtyDaysAgo) > 0 ? FIRST_POSSIBLE_DATE : thirtyDaysAgo;
    let endDate = todayDate;

    if (!isEmptyString(startTime)) {
      try {
        const parsedDate = parseDate(startTime);
        startDate = FIRST_POSSIBLE_DATE.compare(parsedDate) > 0 ? FIRST_POSSIBLE_DATE : parsedDate;
      } catch {
        // Keep default
      }
    }

    if (!isEmptyString(endTime)) {
      try {
        const parsedDate = parseDate(endTime);
        endDate = todayDate.compare(parsedDate) < 0 ? todayDate : parsedDate;
      } catch {
        // Keep default
      }
    }

    return { start: startDate, end: endDate };
  }, [startTime, endTime]);

  // Sorting state
  const sorting = useMemo(() => {
    if (!isEmptyString(sortBy) && !isEmptyString(sortDir)) {
      return { id: sortBy, desc: sortDir === 'desc' };
    }
    // Default: sort by sentPercentage descending
    return { id: 'sentPercentage', desc: true };
  }, [sortBy, sortDir]);

  // Fetch deliverability metrics
  const metricsQuery = servicesQuery.getDeliverabilityMetrics.useQuery({
    queryKey: ['deliverability-metrics', workspaceId, dateRange.start.toString(), dateRange.end.toString()],
    queryData: {
      params: { environment_id: workspaceId ?? 0 },
      query: {
        version: '2',
        start: Math.floor(dateRange.start.toDate(getLocalTimeZone()).getTime() / 1000),
        end: Math.floor(dateRange.end.toDate(getLocalTimeZone()).getTime() / 1000),
        res: 'days',
      },
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: workspaceId != null,
  });

  // Transform API data to table rows
  const rows = useMemo<DeliverabilityMetricRow[]>(() => {
    if (metricsQuery.data?.body.inbox_provider_metrics == null) {
      return [];
    }
    return transformMetricsToRows(metricsQuery.data.body.inbox_provider_metrics);
  }, [metricsQuery.data]);

  // Handlers
  const handleDateRangeChange = useCallback(
    (newRange: DeliverabilityDateRange) => {
      void setStartTime(newRange.start.toString());
      void setEndTime(newRange.end.toString());
    },
    [setStartTime, setEndTime],
  );

  const handleSortingChange = useCallback(
    (newSorting: { id: string; desc: boolean } | null) => {
      if (newSorting == null) {
        void setSortBy(null);
        void setSortDir(null);
      } else {
        void setSortBy(newSorting.id);
        void setSortDir(newSorting.desc ? 'desc' : 'asc');
      }
    },
    [setSortBy, setSortDir],
  );

  const handleSearchChange = useCallback(
    (searchTerm: string) => {
      void setSearch(isEmptyString(searchTerm) ? null : searchTerm);
    },
    [setSearch],
  );

  return {
    // Data
    rows,
    dateRange,
    sorting,
    search: search ?? '',

    // Query state
    isLoading: metricsQuery.isLoading,
    isError: metricsQuery.isError,
    error: metricsQuery.error,
    hasNoData: hasNoData(rows),

    // Handlers
    handleDateRangeChange,
    handleSortingChange,
    handleSearchChange,
  };
}

/**
 * Check if workspace uses custom SMTP (mail servers)
 * For MVP, return false - can add later when backend provides endpoint
 */
export function useHasMailServers() {
  return {
    hasMailServers: false,
    isLoading: false,
  };
}
